import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { formatTime } from '../utils/formatDate';
import { getTeamAbbreviation } from '../utils/teamUtils';
import TeamLogo from './TeamLogo';

const MatchCard = ({ prediction }) => {
  const {
    homeTeam,
    awayTeam,
    matchDate,
    prediction: predictionText,
    confidence,
    odds,
    slug,
    result
  } = prediction;
  
  // Форматирование даты и времени
  const time = formatTime(matchDate); 
  
  // Получение эмодзи для уровня уверенности
  const getConfidenceEmoji = (confidence) => {
    const stars = confidence.match(/⭐/g)?.length || 0;
    if (stars >= 4) return '🔥';
    if (stars >= 3) return '💪';
    if (stars >= 2) return '👍';
    return '🤔';
  };
  
  // Извлечение краткого прогноза (первое предложение) с сокращением названий команд
  const getShortPrediction = (fullPrediction, homeTeam, awayTeam) => {
    const sentences = fullPrediction.split(/[.!?]/);
    let shortText = sentences[0].trim() + (sentences.length > 1 ? '...' : '');
    
    // Простая case-insensitive замена без избыточной сложности
    const replaceTeam = (text, teamName, abbreviation) => {
      if (abbreviation === teamName) return text;
      
      // Экранируем только необходимые символы для regex
      const escaped = teamName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      text = text.replace(new RegExp(escaped, 'gi'), abbreviation);
      
      // Дополнительная замена для команд с дефисами (основной edge case)
      if (teamName.includes('-')) {
        const withoutDash = teamName.replace(/-/g, ' ');
        const escapedNoDash = withoutDash.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        text = text.replace(new RegExp(escapedNoDash, 'gi'), abbreviation);
      }
      
      return text;
    };
    
    // Заменяем названия команд на сокращения
    const homeAbbr = getTeamAbbreviation(homeTeam);
    const awayAbbr = getTeamAbbreviation(awayTeam);
    
    shortText = replaceTeam(shortText, homeTeam, homeAbbr);
    shortText = replaceTeam(shortText, awayTeam, awayAbbr);
    
    return shortText;
  };

  const confidenceEmoji = getConfidenceEmoji(confidence);
  const shortPrediction = getShortPrediction(predictionText, homeTeam, awayTeam);

  return (
    <Link to={`/${slug}`}>
      <motion.div
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-xl transition-all duration-0"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
        whileHover={{ y: -5 }}
      >
        {/* Главный контент */}
        <div className="p-5">
          {/* Команды с большими логотипами */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3 min-w-0 flex-1">
              <TeamLogo 
                teamName={homeTeam}
                className="w-10 h-10 flex-shrink-0"
                alt={`${homeTeam} logo`}
                width={40}
                height={40}
              />
              <div className="min-w-0 flex-1">
                <h3 className="team-name-mobile" title={homeTeam}>
                  <span className="sm:hidden">{getTeamAbbreviation(homeTeam)}</span>
                  <span className="hidden sm:inline">{homeTeam}</span>
                </h3>
                <div className="text-xs text-gray-500 dark:text-gray-400">Home</div>
              </div>
            </div>
            
            <div className="text-center px-4 flex-shrink-0">
              {result?.matchStatus === 'finished' && result.finalScore ? (
                <div>
                  <div className="text-2xl font-bold text-gray-800 dark:text-gray-200">{result.finalScore}</div>
                  <div className="text-xs text-green-600 dark:text-green-500 mt-1 font-semibold">Finished</div>
                </div>
              ) : (
                <>
                  <div className="text-2xl font-bold text-gray-400 dark:text-gray-500">VS</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{time}</div>
                </>
              )}
            </div>
            
            <div className="flex items-center space-x-3 min-w-0 flex-1 justify-end">
              <div className="text-right min-w-0 flex-1">
                <h3 className="team-name-mobile" title={awayTeam}>
                  <span className="sm:hidden">{getTeamAbbreviation(awayTeam)}</span>
                  <span className="hidden sm:inline">{awayTeam}</span>
                </h3>
                <div className="text-xs text-gray-500 dark:text-gray-400">Away</div>
              </div>
              <TeamLogo 
                teamName={awayTeam}
                className="w-10 h-10 flex-shrink-0"
                alt={`${awayTeam} logo`}
                width={40}
                height={40}
              />
            </div>
          </div>

          {/* Краткий прогноз в выделенном блоке */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-4 mb-4 border-l-4 border-primary">
            <div className="flex items-start justify-between gap-3">
              {/* Левая часть - прогноз */}
              <div className="flex items-center space-x-3 flex-1 min-w-0">
                <div className="text-2xl flex-shrink-0">{confidenceEmoji}</div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Prediction</div>
                  <p className="text-gray-900 dark:text-white font-medium leading-relaxed truncate">{shortPrediction}</p>
                </div>
              </div>
              
              {/* Правая часть - уверенность */}
              <div className="flex-shrink-0 text-right">
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Confidence</div>
                <div className="bg-white dark:bg-gray-800 px-2 py-0.5 rounded-full border border-gray-200 dark:border-gray-600">
                  <span className="font-bold text-gray-900 dark:text-white" style={{ fontSize: '10px' }}>{confidence}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Минималистичная таблица коэффициентов */}
          {odds && (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
              <div className="grid grid-cols-3 gap-2 text-center text-sm">
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500 dark:text-gray-400 mb-1">Home</span>
                  <span className="font-bold text-gray-900 dark:text-white">{odds.home || '-'}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500 dark:text-gray-400 mb-1">Draw</span>
                  <span className="font-bold text-gray-900 dark:text-white">{odds.draw || '-'}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500 dark:text-gray-400 mb-1">Away</span>
                  <span className="font-bold text-gray-900 dark:text-white">{odds.away || '-'}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </Link>
  );
};

export default MatchCard; 